import {
  Col,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
  FormGroup,
  InputGroupText,
  InputGroup,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, Fragment, useEffect } from "react";
import { Btn, H5, P } from "../../../../../AbstractElements";
import Cleave from "cleave.js/react";
import Swal from "sweetalert2";
import { Save } from "react-feather";

//TYPES
import { ViewGender } from "../../../../../Types/MasterData/Gender";
import { ViewCivilStatus } from "../../../../../Types/MasterData/CivilStatus";
import { Nationality } from "../../../../../Types/MasterData/Nationalities";
import { JobTitles } from "../../../../../Types/MasterData/JobTitles";

//SERVICES
import {
  applicantsService,
  APPLICANTS_QUERY_KEY,
} from "../../../../../Services/applicantsService";
import {
  VIEW_GENDER_QUERY_KEY,
  genderService,
} from "../../../../../Services/genderService";
import {
  VIEW_CIVILSTATUS_QUERY_KEY,
  civilstatusService,
} from "../../../../../Services/civilstatusService";
import {
  NATIONALITY_QUERY_KEY,
  nationalityService,
} from "../../../../../Services/nationalityService";
import {
  JOBTITLES_QUERY_KEY,
  jobtitleService,
} from "../../../../../Services/jobtitlesService";

const INITIAL_STATE = {
  job_title_id: 0,
  first_name: "",
  last_name: "",
  middle_name: "",
  suffix: "",
  birthdate: "",
  gender: "",
  marital_status: "",
  nationality: "",
  email_address: "",
  mobile1: "",
  mobile2: "",
  telno: "",
  umid: "",
  tin: "",
  national_id: "",
  sss_id: "",
  pagibig_id: "",
  philhealth_id: "",
  ed_level: "",
  ed_status: "",
  course: "",
  school_name: "",
  year: "",
  honor: "",
};

interface UploadFormProps {
  toggle?: () => void;
  ApplNo: string;
}

const UploadProfileForm: React.FC<UploadFormProps> = ({ toggle, ApplNo }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(INITIAL_STATE);

  const { data: applicationData, isLoading: isFetching } = useQuery({
    queryKey: [APPLICANTS_QUERY_KEY, ApplNo],
    queryFn: () => applicantsService.getApplicantsByApplicationNo(ApplNo),
  });

  useEffect(() => {
    if (applicationData) {
      setFormData((prev) => ({
        ...prev,
        first_name: applicationData.data.first_name || "",
        last_name: applicationData.data.last_name || "",
        middle_name: applicationData.data.middle_name || "",
        suffix: applicationData.data.suffix || "",
        birthdate: applicationData.data.birthdate
          ? new Date(applicationData.data.birthdate).toLocaleDateString("en-CA")
          : "",
        gender: applicationData.data.gender || "",
        marital_status: applicationData.data.marital_status || "",
        nationality: applicationData.data.nationality || "",
        email_address: applicationData.data.email_address || "",
        mobile1: applicationData.data.mobile1 || "",
        mobile2: applicationData.data.mobile2 || "",
        telno: applicationData.data.telno || "",
        umid: applicationData.data.umid || "",
        tin: applicationData.data.tin || "",
        national_id: applicationData.data.national_id || "",
        sss_id: applicationData.data.sss_id || "",
        pagibig_id: applicationData.data.pagibig_id || "",
        philhealth_id: applicationData.data.philhealth_id || "",
        ed_level: applicationData.data.ed_level || "",
        ed_status: applicationData.data.ed_status || "",
        school_name: applicationData.data.school_name || "",
        course: applicationData.data.course || "",
        year: applicationData.data.year || "",
        honor: applicationData.data.honor || "",
        job_title_id: applicationData.data.job_title_id || "",
      }));
    }
  }, [applicationData]);

  //Get Gender Option
  const { data: genderData, isLoading: GenderisLoading } = useQuery<{
    data: ViewGender[];
  }>({
    queryKey: VIEW_GENDER_QUERY_KEY,
    queryFn: genderService.getAllViewGender,
  });

  const genders: ViewGender[] =
    genderData?.data?.filter((item) => item.status_label === "ACTIVE") ?? [];

  //Get Civil Status Option
  const { data: civilstatusData, isLoading: CivilStatusisLoading } = useQuery<{
    data: ViewCivilStatus[];
  }>({
    queryKey: VIEW_CIVILSTATUS_QUERY_KEY,
    queryFn: civilstatusService.getAllViewCivilStatus,
  });

  const civilstatus: ViewCivilStatus[] =
    civilstatusData?.data?.filter((item) => item.status_label === "ACTIVE") ??
    [];

  //Get Nationality Option
  const { data: nationalityData, isLoading: NationalityisLoading } = useQuery<{
    data: Nationality[];
  }>({
    queryKey: NATIONALITY_QUERY_KEY,
    queryFn: nationalityService.getAllNationalities,
  });

  const nationalities: Nationality[] =
    nationalityData?.data?.filter((item) => item.status === 1) ?? [];

  //Get Job Title Option
  const { data: jobData, isLoading: JobsisLoading } = useQuery<{
    data: JobTitles[];
  }>({
    queryKey: JOBTITLES_QUERY_KEY,
    queryFn: jobtitleService.getAllJobTitles,
  });

  const jobtitles: JobTitles[] =
    jobData?.data?.filter((item) => item.status === 1) ?? [];

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      applicantsService.updateApplicantInfo(ApplNo, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: result.message,
        confirmButtonColor: "#4e73df",
      });
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Error",
        text: responseData?.message || "Failed to update application form.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update this record?",
      icon: "question",
      showCancelButton: true,

      confirmButtonText: '<i class="fa fa-save"></i> Update Information',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',

      confirmButtonColor: "#4e73df", // Primary
      cancelButtonColor: "#dc3545", // Danger
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Uploading...",
          text: "Please wait while your profile photo is being uploaded.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        updateMutation.mutate(formData);
      }
    });
  };

  return (
    <ModalBody className="large-modal-body modal-body">
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          {/* START STEP 1 PERSONAL INFORMATION */}
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <div className="d-flex justify-content-between align-items-center">
              <H5 className="mt-1 mb-2">Personal Information</H5>
            </div>
            <Row className="p-1">
              <Col md="4">
                <FormGroup>
                  <Label>
                    First Name <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-user txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      id="first_name"
                      name="first_name"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.first_name
                            ? formData.first_name
                            : ""
                      }
                      required
                      minLength={2}
                      maxLength={150}
                      onChange={handleInputChange}
                      placeholder="Enter your first name"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>Middle Name</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-user txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      id="middle_name"
                      name="middle_name"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.middle_name
                            ? formData.middle_name
                            : ""
                      }
                      minLength={2}
                      maxLength={150}
                      onChange={handleInputChange}
                      placeholder="Enter your middle name"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Last Name <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-user txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      id="last_name"
                      name="last_name"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.last_name
                            ? formData.last_name
                            : ""
                      }
                      minLength={2}
                      maxLength={150}
                      required
                      onChange={handleInputChange}
                      placeholder="Enter your surname"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="2">
                <FormGroup>
                  <Label>Sufix Name</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-user txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      id="suffix"
                      name="suffix"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.suffix
                            ? formData.suffix
                            : ""
                      }
                      minLength={2}
                      maxLength={150}
                      required
                      onChange={handleInputChange}
                      placeholder="Enter your surname"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Date of Birth <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="date"
                      id="birthdate"
                      name="birthdate"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.birthdate
                            ? formData.birthdate
                            : ""
                      }
                      required
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Gender <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="select"
                      id="geneder"
                      name="gender"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.gender
                            ? formData.gender
                            : ""
                      }
                      required
                      onChange={handleInputChange}
                    >
                      <option value="" disabled>
                        {GenderisLoading ? "Loading..." : "Select Gender"}
                      </option>
                      {!GenderisLoading &&
                        genders.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Civil Status <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-heart txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="select"
                      id="marital_status"
                      name="marital_status"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.marital_status
                            ? formData.marital_status
                            : ""
                      }
                      required
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {CivilStatusisLoading
                          ? "Loading..."
                          : "Select Civil Status"}
                      </option>
                      {!CivilStatusisLoading &&
                        civilstatus.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Nationality <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-flag txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="select"
                      id="nationality"
                      name="nationality"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.nationality
                            ? formData.nationality
                            : ""
                      }
                      required
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {NationalityisLoading
                          ? "Loading..."
                          : "Select Nationality"}
                      </option>
                      {!NationalityisLoading &&
                        nationalities.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>
          {/* END STEP 1 PERSONAL INFORMATION */}

          {/* START STEP 2 CONTACT DETAILS */}
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <div className="d-flex justify-content-between align-items-center">
              <H5 className="mt-1 mb-2">Contact Details</H5>
            </div>
            <Row className="p-1">
              <Col md="6">
                <FormGroup>
                  <Label>
                    Email Address <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-envelope txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="email"
                      id="email_address"
                      name="email_address"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.email_address
                            ? formData.email_address
                            : ""
                      }
                      minLength={15}
                      maxLength={250}
                      placeholder="Enter your email address"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Mobile Number <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-touch-phone txt-primary"></i>
                    </InputGroupText>
                    <Cleave
                      className="form-control"
                      options={{
                        delimiter: "-",
                        blocks: [4, 3, 4],
                        uppercase: true,
                      }}
                      required
                      id="mobile2"
                      name="mobile2"
                      type="text"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.mobile1
                            ? formData.mobile1
                            : ""
                      }
                      placeholder="Enter Mobile No."
                      minLength={13}
                      maxLength={13}
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>Alternative Mobile Number</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-touch-phone txt-primary"></i>
                    </InputGroupText>
                    <Cleave
                      className="form-control"
                      options={{
                        delimiter: "-",
                        blocks: [4, 3, 4],
                        uppercase: true,
                      }}
                      id="mobile2"
                      name="mobile2"
                      type="text"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.mobile2
                            ? formData.mobile2
                            : ""
                      }
                      placeholder="Enter Alternative Mobile No."
                      minLength={13}
                      maxLength={13}
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>Telephone Number</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-phone txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      id="telno"
                      name="telno"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.telno
                            ? formData.telno
                            : ""
                      }
                      minLength={10}
                      maxLength={20}
                      placeholder="Enter your telephone no"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>
          {/* END STEP 2 CONTACT INFORMATION */}

          {/* START STEP 3 GEVERNMENT ID */}
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <div className="d-flex justify-content-between align-items-center">
              <H5 className="mt-1 mb-2">Government IDs</H5>
            </div>
            <Row className="p-1">
              <Col md="4">
                <FormGroup>
                  <Label>
                    SSS <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-id txt-primary"></i>
                    </InputGroupText>
                    <Cleave
                      className="form-control"
                      options={{
                        delimiter: "-",
                        blocks: [2, 7, 1],
                        uppercase: true,
                      }}
                      id="sss_id"
                      name="sss_id"
                      type="text"
                      required
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.sss_id
                            ? formData.sss_id
                            : ""
                      }
                      placeholder="Enter your SSS Id"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>
                    PhilHealth <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-id txt-primary"></i>
                    </InputGroupText>
                    <Cleave
                      className="form-control"
                      options={{
                        delimiter: "-",
                        blocks: [2, 9, 1],
                        uppercase: true,
                      }}
                      id="philhealth_id"
                      name="philhealth_id"
                      type="text"
                      required
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.philhealth_id
                            ? formData.philhealth_id
                            : ""
                      }
                      placeholder="Enter your PhilHealth Id"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>
                    Pag-IBIG <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-id txt-primary"></i>
                    </InputGroupText>
                    <Cleave
                      className="form-control"
                      options={{
                        delimiter: "-",
                        blocks: [4, 4, 4],
                        uppercase: true,
                      }}
                      id="pagibig_id"
                      name="pagibig_id"
                      type="text"
                      required
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.pagibig_id
                            ? formData.pagibig_id
                            : ""
                      }
                      placeholder="Enter your Pag-IBIG Id"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>TIN</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-id txt-primary"></i>
                    </InputGroupText>
                    <Cleave
                      className="form-control"
                      options={{
                        delimiter: "-",
                        blocks: [3, 3, 3, 3],
                        uppercase: true,
                      }}
                      id="tin"
                      name="tin"
                      type="text"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.tin
                            ? formData.tin
                            : ""
                      }
                      placeholder="Enter your TIN"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>UMID</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-id txt-primary"></i>
                    </InputGroupText>
                    <Cleave
                      className="form-control"
                      options={{
                        delimiter: "-",
                        blocks: [4, 7, 1],
                        uppercase: true,
                      }}
                      id="umid"
                      name="umid"
                      type="text"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.umid
                            ? formData.umid
                            : ""
                      }
                      placeholder="Enter your UMID"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>National ID</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-id txt-primary"></i>
                    </InputGroupText>
                    <Cleave
                      className="form-control"
                      options={{
                        delimiter: "-",
                        blocks: [4, 4, 4, 4],
                        uppercase: true,
                      }}
                      id="national_id"
                      name="national_id"
                      type="text"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.national_id
                            ? formData.national_id
                            : ""
                      }
                      placeholder="Enter your National Id"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>
          {/* END STEP 3 GEVERNMENT ID */}

          {/* START STEP 4 HIGHEST EDUCATIONAL ATTAINMENT */}
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <div className="d-flex justify-content-between align-items-center">
              <H5 className="mt-1 mb-2">Highest Educational Attainment</H5>
            </div>
            <Row className="p-1">
              <Col md="6">
                <FormGroup>
                  <Label>
                    School Name <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-university txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="form-control"
                      id="school_name"
                      name="school_name"
                      type="text"
                      required
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.school_name
                            ? formData.school_name
                            : ""
                      }
                      placeholder="Enter your Shool Name"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>Course / Strand</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-instrument txt-primary"></i>
                    </InputGroupText>
                    <Input
                      className="form-control"
                      id="course"
                      name="course"
                      type="text"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.course
                            ? formData.course
                            : ""
                      }
                      placeholder="Enter your Shool Name"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Educational Level <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-hat-alt txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="ed_level"
                      name="ed_level"
                      type="select"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.ed_level
                            ? formData.ed_level
                            : ""
                      }
                      required
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                    >
                      <option value="" selected>
                        Select Level
                      </option>
                      <option value="Elementary">Elementary</option>
                      <option value="Junior High School">
                        Junior High School
                      </option>
                      <option value="Senior High School">
                        Senior High School
                      </option>
                      <option value="Vocational / TESDA">
                        Vocational / TESDA
                      </option>
                      <option value="College">College</option>
                      <option value="Postgraduate / Master’s">
                        Postgraduate / Master’s
                      </option>
                      <option value="Doctorate (PhD)">Doctorate (PhD)</option>
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Educational Status <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="ed_status"
                      name="ed_status"
                      type="select"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.ed_status
                            ? formData.ed_status
                            : ""
                      }
                      required
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                    >
                      <option value="" selected>
                        Select Status
                      </option>
                      <option value="Undergraduate">Undergraduate</option>
                      <option value="Graduate">Graduate</option>
                      <option value="Ongoing">Ongoing</option>
                      <option value="Completed">Completed</option>
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>
                    Year <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="year"
                      name="year"
                      type="select"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.year
                            ? formData.year
                            : ""
                      }
                      required
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                    >
                      <option value="" selected>
                        Select Year
                      </option>
                      {Array.from({ length: 50 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup>
                  <Label>Honor</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-certificate-alt-1 txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="honor"
                      name="honor"
                      type="text"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.honor
                            ? formData.honor
                            : ""
                      }
                      placeholder="Enter your honor"
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>
          {/* END STEP 5 HIGHEST EDUCATIONAL ATTAINMENT */}

          {/* START STEP 6 POSITION APPLIED FOR */}
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <div className="d-flex justify-content-between align-items-center">
              <H5 className="mt-1 mb-2">Position Applied For</H5>
            </div>
            <Row className="p-1">
              <Col md="6">
                <FormGroup>
                  <Label>
                    Job Title <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="job_title_id"
                      name="job_title_id"
                      type="select"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.job_title_id
                            ? formData.job_title_id
                            : ""
                      }
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                      required
                    >
                      <option value="" selected>
                        {JobsisLoading ? "Loading..." : "Select Job Title"}
                      </option>
                      {!JobsisLoading &&
                        jobtitles.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.job_title}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>
          {/* END STEP 6 POSITION APPLIED FOR */}

          <div className="d-flex justify-content-end">
            <Col md="4">
              <Btn
                color="primary"
                className="w-100"
                type="submit"
                disabled={updateMutation.isPending}
              >
                <Save size={16} className="me-1" />
                {updateMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updateding Applicants Information...
                  </>
                ) : (
                  "Update"
                )}
              </Btn>
            </Col>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UploadProfileForm;
